import { useTranslation } from '@hbs-us/ui';
import { useLocation } from '@tanstack/react-router';
import { X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'qelm-blocked-route';
const EVENT_NAME = 'qelm:blocked-route';

export type BlockedRouteReason = 'feature' | 'permission';

/** Why the guard sent someone away, and the path it sent them to. */
export interface BlockedRoute {
  reason: BlockedRouteReason;
  target: string;
}

function isBlockedRoute(value: unknown): value is BlockedRoute {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<BlockedRoute>;
  return (
    (candidate.reason === 'feature' || candidate.reason === 'permission') &&
    typeof candidate.target === 'string'
  );
}

/**
 * Called by the route guard just before it sends someone away from a page they cannot open.
 * `target` is the path the guard is sending them to: the notice only shows while they are on it.
 *
 * Two channels, because the guard fires in two situations. On a full page load the notice is not
 * mounted yet, so the entry waits in sessionStorage. On an in-app navigation the notice is already
 * mounted and never remounts, so it hears about it from the event.
 */
export function rememberBlockedRoute(reason: BlockedRouteReason, target: string): void {
  const entry: BlockedRoute = { reason, target };
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Storage can be blocked; the redirect still happens, only the explanation is lost.
  }
  window.dispatchEvent(new CustomEvent<BlockedRoute>(EVENT_NAME, { detail: entry }));
}

function takeBlockedRoute(): BlockedRoute | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return isBlockedRoute(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Says why someone is here instead of where they clicked. The guard deliberately redirects rather
 * than showing a wall; this keeps it from being silent. It shows on the page the guard landed
 * them on and goes away as soon as they move on, so it never follows them around the app.
 */
export function BlockedRouteNotice() {
  const { t } = useTranslation();
  const pathname = useLocation({ select: (location) => location.pathname });
  const [entry, setEntry] = useState<BlockedRoute | null>(null);
  // The event fires from `beforeLoad`, before the location commits, so the pathname is still the
  // old one when the entry arrives. "Left the target" only means something once it was reached.
  const reachedTarget = useRef(false);

  useEffect(() => {
    const stored = takeBlockedRoute();
    if (stored) {
      reachedTarget.current = false;
      setEntry(stored);
    }
    function onBlocked(event: Event) {
      const detail = (event as CustomEvent<unknown>).detail;
      if (!isBlockedRoute(detail)) return;
      // The event carries it, so the stored copy would only resurface on a later page load.
      takeBlockedRoute();
      reachedTarget.current = false;
      setEntry(detail);
    }
    window.addEventListener(EVENT_NAME, onBlocked);
    return () => window.removeEventListener(EVENT_NAME, onBlocked);
  }, []);

  useEffect(() => {
    if (!entry) return;
    if (pathname === entry.target) {
      reachedTarget.current = true;
    } else if (reachedTarget.current) {
      setEntry(null);
    }
  }, [entry, pathname]);

  const reason = entry && pathname === entry.target ? entry.reason : null;
  if (!reason) return null;
  return (
    <div
      role="status"
      className="mx-4 mt-4 flex items-start justify-between gap-3 rounded-md border border-border bg-card px-4 py-3 text-foreground text-sm sm:mx-6 lg:mx-8"
    >
      <p className="py-1">
        {t(
          reason === 'feature'
            ? 'common:notAvailable.featureBody'
            : 'common:notAvailable.permissionBody',
        )}
      </p>
      <button
        type="button"
        onClick={() => setEntry(null)}
        aria-label={t('common:notAvailable.dismiss')}
        className="-my-2 -mr-2 inline-flex size-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <X className="size-4" aria-hidden="true" />
      </button>
    </div>
  );
}
